import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress, Alert, IconButton, Chip, Tabs, Tab
} from '@mui/material';
import { Refresh, CheckCircle, Cancel, Payment } from '@mui/icons-material';
import axios from 'axios';

const API = 'https://api.bharatdarshan.app/api';

const STATUS_COLORS = { pending: 'warning', approved: 'info', paid: 'success', rejected: 'error' };
const STATUS_LABELS = { pending: 'Pending', approved: 'Approved', paid: 'Paid', rejected: 'Rejected' };

const SanataniSettlements = () => {
  const [loading, setLoading] = useState(true);
  const [withdrawals, setWithdrawals] = useState([]);
  const [summary, setSummary] = useState({});
  const [tab, setTab] = useState('all');
  const [actionDialog, setActionDialog] = useState(null);
  const [payoutRef, setPayoutRef] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [snack, setSnack] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/sanatani-hotel/admin/withdrawals`);
      setWithdrawals(r.data?.data || []);
      setSummary(r.data?.summary || {});
    } catch (e) {
      setSnack({ msg: 'Failed to load withdrawals', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openAction = (w, action) => {
    setActionDialog({ ...w, action });
    setPayoutRef('');
    setAdminNote('');
  };
  const closeAction = () => { setActionDialog(null); setPayoutRef(''); setAdminNote(''); };

  const doAction = async () => {
    if (!actionDialog) return;
    setBusy(true);
    try {
      await axios.put(
        `${API}/sanatani-hotel/admin/withdrawal/${actionDialog.partnerId}/${actionDialog._idx}`,
        { action: actionDialog.action, payoutRef, adminNote }
      );
      setSnack({ msg: `Withdrawal ${actionDialog.action}d successfully`, type: 'success' });
      closeAction();
      load();
    } catch (e) {
      setSnack({ msg: e.response?.data?.message || 'Action failed', type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const fmt = (n) => '\u20B9' + (Number(n) || 0).toLocaleString('en-IN');
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '\u2014';

  const filtered = tab === 'all' ? withdrawals : withdrawals.filter((w) => w.status === tab);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700} color="#795548">
          Sanatani Sadan \u2014 Partner Settlements
        </Typography>
        <Button startIcon={<Refresh />} onClick={load} variant="outlined" sx={{ borderColor: '#795548', color: '#795548' }}>
          Refresh
        </Button>
      </Box>

      {snack && (
        <Alert severity={snack.type} sx={{ mb: 2 }} onClose={() => setSnack(null)}>{snack.msg}</Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Requests', value: summary.total || 0, color: '#795548' },
          { label: 'Pending', value: summary.pending || 0, color: '#FF9800', sub: fmt(summary.totalPending) },
          { label: 'Approved', value: summary.approved || 0, color: '#2196F3' },
          { label: 'Paid', value: summary.paid || 0, color: '#4CAF50', sub: fmt(summary.totalPaid) },
          { label: 'Rejected', value: summary.rejected || 0, color: '#f44336' },
        ].map((s) => (
          <Grid item xs={12} sm={6} md={2.4} key={s.label}>
            <Card sx={{ borderTop: `4px solid ${s.color}` }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                <Typography variant="h5" fontWeight={700} color={s.color}>{s.value}</Typography>
                {s.sub && <Typography variant="caption" color="text.secondary">{s.sub}</Typography>}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Withdrawals Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ background: 'linear-gradient(135deg, #795548, #a1887f)', color: '#fff', p: 2 }}>
          <Typography variant="h6" fontWeight={600}>Withdrawal Requests</Typography>
        </Box>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label="All" value="all" />
          <Tab label="Pending" value="pending" />
          <Tab label="Approved" value="approved" />
          <Tab label="Paid" value="paid" />
          <Tab label="Rejected" value="rejected" />
        </Tabs>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: '#f5f5f5' }}>
                  <TableCell><b>Partner</b></TableCell>
                  <TableCell><b>Mobile</b></TableCell>
                  <TableCell><b>Bank Details</b></TableCell>
                  <TableCell align="right"><b>Amount</b></TableCell>
                  <TableCell><b>Requested</b></TableCell>
                  <TableCell><b>Processed</b></TableCell>
                  <TableCell><b>Status</b></TableCell>
                  <TableCell><b>Payout Ref</b></TableCell>
                  <TableCell><b>Note</b></TableCell>
                  <TableCell align="center"><b>Actions</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={10} align="center">No withdrawal requests</TableCell></TableRow>
                )}
                {filtered.map((w, i) => (
                  <TableRow key={i} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{w.businessName}</Typography>
                      <Typography variant="caption" color="text.secondary">{w.ownerName}</Typography>
                    </TableCell>
                    <TableCell>{w.mobile}</TableCell>
                    <TableCell>
                      {w.bankDetails && w.bankDetails.accountNumber ? (
                        <Box>
                          <Typography variant="caption" display="block">{w.bankDetails.accountName}</Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            A/C: {w.bankDetails.accountNumber}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            IFSC: {w.bankDetails.ifscCode}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">No bank details</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={700} color="#795548">{fmt(w.amount)}</Typography>
                    </TableCell>
                    <TableCell>{fmtDate(w.requestedAt)}</TableCell>
                    <TableCell>{fmtDate(w.processedAt)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={STATUS_LABELS[w.status] || w.status} color={STATUS_COLORS[w.status] || 'default'} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{w.payoutRef || '\u2014'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{w.adminNote || '\u2014'}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      {w.status === 'pending' && (
                        <>
                          <IconButton size="small" color="info" title="Approve" onClick={() => openAction(w, 'approve')}>
                            <CheckCircle fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" title="Reject" onClick={() => openAction(w, 'reject')}>
                            <Cancel fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      {w.status === 'approved' && (
                        <>
                          <IconButton size="small" color="success" title="Mark Paid" onClick={() => openAction(w, 'pay')}>
                            <Payment fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" title="Reject" onClick={() => openAction(w, 'reject')}>
                            <Cancel fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Action Dialog */}
      <Dialog open={!!actionDialog} onClose={closeAction} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{
            background: actionDialog?.action === 'reject' ? '#f44336' : actionDialog?.action === 'pay' ? '#4CAF50' : '#2196F3',
            color: '#fff',
          }}
        >
          {actionDialog?.action === 'approve' && 'Approve Withdrawal'}
          {actionDialog?.action === 'pay' && 'Mark as Paid'}
          {actionDialog?.action === 'reject' && 'Reject Withdrawal'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {actionDialog && (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Partner: <b>{actionDialog.businessName}</b> | Amount: <b>{fmt(actionDialog.amount)}</b>
              </Typography>
              {actionDialog.action === 'pay' && (
                <TextField
                  label="Payout Reference"
                  fullWidth
                  value={payoutRef}
                  onChange={(e) => setPayoutRef(e.target.value)}
                  sx={{ mb: 2 }}
                  placeholder="UTR / Transaction ID"
                />
              )}
              <TextField
                label="Admin Note (optional)"
                fullWidth
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder="Internal note"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAction} disabled={busy}>Cancel</Button>
          <Button
            onClick={doAction}
            disabled={busy}
            variant="contained"
            color={actionDialog?.action === 'reject' ? 'error' : actionDialog?.action === 'pay' ? 'success' : 'info'}
          >
            {busy ? <CircularProgress size={18} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SanataniSettlements;
